/**
 * Privacy Policy — wavedsp.ai/privacy
 *
 * Public, unauthenticated. Purpose: pass Meta App Review, TikTok
 * Content Posting API review, YouTube Data API verification, and
 * meet GDPR / CCPA / CPRA disclosure requirements.
 *
 * Plain-English voice + every required disclosure. If you change
 * what data is collected, update this page AND notify users in-app.
 */
import React, { useEffect } from 'react';
import LegalLayout from '../../components/legal/LegalLayout';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Privacy Policy · WAVE DSP';
    return () => { document.title = prev; };
  }, []);

  return (
    <LegalLayout title="Privacy Policy" lastUpdated="June 27, 2026">
      <p>
        WAVE DSP (&ldquo;<strong>WAVE</strong>,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;)
        operates a closed-loop ad-tech platform that connects brands with social-media
        creators for paid sponsored content. This policy explains <strong>what data
        we collect, how we use it, and what rights you have over it</strong>.
      </p>

      <p>
        We wrote this in plain English. If anything is unclear, email{' '}
        <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a> and we&rsquo;ll
        clarify in writing within 5 business days.
      </p>

      <h2>1. Who this policy applies to</h2>
      <p>This policy covers three groups of users:</p>
      <ul>
        <li><strong>Brands</strong>: businesses that build and fund waves on the platform.</li>
        <li><strong>Creators</strong>: individuals (18+) who accept paid briefs and publish sponsored content.</li>
        <li><strong>Audiences</strong>: viewers who interact with sponsored content (e.g., RSVPs, surveys, reviews).</li>
      </ul>

      <h2>2. What we collect &mdash; and why</h2>

      <h3>From brands</h3>
      <ul>
        <li>Account info: name, email, business address, payment method.</li>
        <li>Wave inputs: campaign goals, SKU specs, KPI weights, briefs.</li>
        <li>Performance data: impressions, clicks, RSVPs, redemptions, returned via creator platforms or our pixel/UTM links.</li>
      </ul>
      <p><em>We use this to run waves, calculate fees, and produce reports.</em></p>

      <h3>From creators</h3>
      <ul>
        <li>Account info: name, email, payout details (handled by Stripe Connect).</li>
        <li>Self-declared profile: niche, audience description, generation, vertical (the &ldquo;6 vectors&rdquo; of our Digital DNA model).</li>
        <li>Linked-platform data via OAuth (with your explicit consent): Instagram username + business account ID, TikTok username + open ID, YouTube channel ID, audience demographics, and the limited read-only permissions enumerated in section 4 below. WAVE never holds or requests publishing scopes on any platform.</li>
        <li>Content you submit, draft, or share through WAVE (briefs, drafts, public post URLs you submit as proof of completion).</li>
      </ul>
      <p><em>We use this to match you to brand briefs, schedule posts, and pay you.</em></p>

      <h3>From audiences</h3>
      <ul>
        <li>Voluntary inputs only: RSVPs, post-event surveys, product reviews submitted at the storefront.</li>
        <li>Aggregate engagement metrics (e.g., a wave reached 124K accounts) returned by social platforms.</li>
      </ul>
      <p>
        <em>We do not buy or use third-party cookies. We do not track audiences across
        unrelated sites. Every audience signal is voluntary, identified at the moment
        of intent, and timestamped.</em>
      </p>

      <h2>3. How we use data</h2>
      <ul>
        <li><strong>To run waves.</strong> Match creators to brands, generate briefs, schedule and publish posts, measure outcomes.</li>
        <li><strong>To pay creators.</strong> Calculate earnings and route payouts via Stripe Connect.</li>
        <li><strong>To prevent fraud and abuse.</strong> Detect bots, verify creator identity, screen content for FTC compliance.</li>
        <li><strong>To improve our matching engine.</strong> Aggregated, de-identified wave outcomes train the Digital DNA model. We never sell or share identified individual data.</li>
        <li><strong>To meet legal obligations.</strong> Tax reporting (1099-NEC for U.S. creators), subpoenas, anti-money-laundering checks.</li>
      </ul>

      <h2>4. Social-platform data we read (and what we don&rsquo;t do)</h2>
      <p>
        When a creator connects an Instagram, TikTok, or YouTube account through
        WAVE, they grant us limited, <strong>read-only</strong> OAuth permission
        to retrieve specific profile and performance data via the platform&rsquo;s
        official API. The exact scopes we request, and what we use each one for,
        are listed below verbatim.
      </p>

      <h3>Instagram (Meta Graph API &mdash; Instagram Login product)</h3>
      <ul>
        <li><code>instagram_business_basic</code> &mdash; to retrieve the creator&rsquo;s
          public profile data (username, profile picture, account type, follower
          count) so brand campaign-planners can verify that the connected account
          matches the creator who enrolled in a wave. We do not retrieve private
          messages, contact lists, or third-party-account data.</li>
        <li><code>instagram_business_manage_insights</code> &mdash; to retrieve
          insights (impressions, reach, engagement, video views) for the specific
          posts a creator publishes for a WAVE-enrolled campaign. Insights are
          retrieved per-<code>media_id</code> at the post-by-post level &mdash; we
          do not enumerate or read insights for any post the creator did not
          publish through WAVE. Data is rolled up into the brand&rsquo;s campaign
          report and discarded 90 days after wave completion.</li>
      </ul>

      <h3>TikTok (TikTok for Developers &mdash; Display API + Login Kit)</h3>
      <ul>
        <li><code>user.info.basic</code> &mdash; to retrieve the creator&rsquo;s
          public username, profile picture, and bio for in-product display and
          brand verification of the connected account.</li>
        <li><code>user.info.stats</code> &mdash; to retrieve publicly visible
          follower count, like count, and video count so brands can match
          creators to campaigns of an appropriate audience scale.</li>
        <li><code>video.list</code> &mdash; to retrieve a list of the creator&rsquo;s
          public videos (title, cover image, view count, public TikTok URL) so
          brands can review the creator&rsquo;s recent content for brand-safety
          and vertical fit. We do not retrieve private or draft videos.</li>
      </ul>
      <p>
        We do <strong>not</strong> request, and our code base does not contain,
        any TikTok Content Posting scopes (e.g., <code>video.publish</code>,
        <code>video.upload</code>). We never publish content to TikTok.
      </p>

      <h3>YouTube (Google API &mdash; YouTube Data v3 + YouTube Analytics)</h3>
      <ul>
        <li><code>https://www.googleapis.com/auth/youtube.readonly</code> &mdash;
          to retrieve the creator&rsquo;s channel info (channel ID, title,
          subscriber count, video count) and a list of their uploaded public
          videos for brand verification and matching.</li>
        <li><code>https://www.googleapis.com/auth/yt-analytics.readonly</code>
          &mdash; to retrieve performance analytics (impressions, click-through
          rate, average view duration) for videos the creator publishes for a
          WAVE-enrolled campaign. Per-channel retrieval is scoped to the
          enrolled <code>videoId</code> values only.</li>
        <li><code>https://www.googleapis.com/auth/userinfo.email</code> +
          <code>openid</code> &mdash; standard OpenID Connect identity scopes to
          identify the connecting Google account.</li>
      </ul>
      <p>
        We do <strong>not</strong> request, and our code base does not contain,
        any YouTube upload scopes (e.g.,
        <code>https://www.googleapis.com/auth/youtube.upload</code>). We never
        publish content to YouTube.
      </p>

      <h3>Publishing model &mdash; notify-and-post</h3>
      <p>
        <strong>WAVE does not publish content on behalf of creators.</strong> Our
        Phase&nbsp;0 publishing architecture is <em>notify-and-post</em>: a brand
        approves a draft inside WAVE; the creator receives a notification; the
        creator publishes the content <em>natively</em> on Instagram, TikTok, or
        YouTube using the platform&rsquo;s own apps; the creator (or the brand
        on the creator&rsquo;s behalf) then submits the public post URL back to
        WAVE so we can attach it to the wave record. We do not hold, request,
        or store any publishing scopes on any platform.
      </p>

      <h3>Revoking access</h3>
      <p>
        Creators may revoke WAVE&rsquo;s read-only access at any time:
      </p>
      <ul>
        <li>Inside WAVE: <em>Settings &rarr; Connected Accounts &rarr; Disconnect</em>.</li>
        <li>On Instagram/Facebook: <em>Settings &rarr; Apps and Websites &rarr; Active &rarr; Remove WAVE</em>.</li>
        <li>On TikTok: <em>Settings &rarr; Manage Account &rarr; Apps and Permissions &rarr; Revoke WAVE</em>.</li>
        <li>On YouTube/Google: <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer noopener">myaccount.google.com/permissions</a>.</li>
      </ul>
      <p>
        Revocation immediately stops any further API reads. Existing wave
        commitments remain in effect &mdash; revocation does not cancel a wave
        already in progress.
      </p>

      <h2>5. Who we share data with</h2>
      <p>We share data only with the parties strictly needed to operate the platform:</p>
      <ul>
        <li><strong>Stripe</strong>: to process payments and creator payouts. <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer noopener">Stripe&rsquo;s privacy policy</a>.</li>
        <li><strong>Meta (Instagram &amp; Facebook), TikTok, YouTube</strong>: to read creator profile data and publish approved content via their APIs.</li>
        <li><strong>Sentry</strong>: to capture crash reports (no personal payload data is included).</li>
        <li><strong>MongoDB Atlas, AWS</strong>: our infrastructure providers; data is encrypted at rest.</li>
        <li><strong>Government authorities</strong>: only when legally compelled (subpoena, court order).</li>
      </ul>
      <p>
        <strong>We do not sell personal data.</strong> We do not share data with ad
        networks, brokers, or for behavioural-advertising purposes outside of our
        own platform.
      </p>

      <h2>6. Your rights</h2>
      <p>Regardless of where you live, you can:</p>
      <ul>
        <li><strong>Access</strong> a copy of the data we hold about you.</li>
        <li><strong>Correct</strong> data that is inaccurate.</li>
        <li><strong>Delete</strong> your account and associated data (subject to tax/legal retention requirements).</li>
        <li><strong>Port</strong> your data to another service.</li>
        <li><strong>Withdraw consent</strong> for any optional processing.</li>
      </ul>
      <p>
        <strong>If you live in California (CCPA / CPRA)</strong>, the EU/UK (GDPR), or
        Canada (PIPEDA), you have these rights by law and we honour them globally.
        Submit a request to <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a>
        and we will respond within 30 days. We will never charge you for exercising
        your rights or retaliate against you for doing so.
      </p>

      <h2>7. How long we keep data</h2>
      <ul>
        <li><strong>Active accounts</strong>: as long as the account exists.</li>
        <li><strong>Closed accounts</strong>: 90 days, then permanent deletion. Tax records retained 7 years where law requires.</li>
        <li><strong>Wave performance data</strong>: aggregated and de-identified after 18 months.</li>
        <li><strong>Backups</strong>: rotated out of cold storage within 90 days of deletion.</li>
      </ul>

      <h2>8. Security</h2>
      <p>
        All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Backend
        services communicate over HMAC-SHA256-signed channels. Access to production
        data is restricted to a small set of engineers under audit logging. We
        disclose any breach affecting your data within 72 hours, as required by
        GDPR Article 33.
      </p>

      <h2>9. Children</h2>
      <p>
        WAVE is for users 18 and over. We do not knowingly collect data from anyone
        under 18. If you believe a minor has registered, email{' '}
        <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a> and we will delete
        the account immediately.
      </p>

      <h2>10. International transfers</h2>
      <p>
        Data is stored in the United States. If you are outside the U.S., your data
        will be transferred and processed there under standard contractual clauses
        approved by the European Commission for non-EU transfers.
      </p>

      <h2>11. Cookies and similar technologies</h2>
      <p>We use a minimal set of cookies, all first-party:</p>
      <ul>
        <li><code>wave_session</code>: keeps you logged in.</li>
        <li><code>wave_inv_session</code>: long-lived investor-page session cookie used to distinguish a returning viewer from a forwarded link. Contains no personal data.</li>
        <li><code>wave_csrf</code>: CSRF protection for form submissions.</li>
      </ul>
      <p>
        We do not use third-party advertising cookies, retargeting pixels, or cross-site
        trackers. We don&rsquo;t need them. Our platform runs on declared, voluntary
        zero-party data.
      </p>

      <h2>12. SMS / Text Messaging</h2>
      <p>
        If you provide your mobile phone number, WAVE may send you transactional SMS
        notifications such as posting-window reminders and campaign deadline alerts.
        Your phone number is collected only through the optional phone field during
        creator registration, and providing it constitutes your opt-in to receive
        these messages. Message frequency varies based on your campaign activity
        (typically 2&ndash;4 messages per campaign). Message and data rates may apply.
        We do not share, sell, or transfer your mobile phone number to third parties
        or affiliates for marketing purposes. Reply STOP to opt out or HELP for
        assistance.
      </p>

      <h2>13. Changes to this policy</h2>
      <p>
        Material changes will be announced via in-app notice and email at least 14 days
        before they take effect. The full revision history is available on request.
      </p>

      <h2>14. Contact and data-deletion requests</h2>
      <p>
        <strong>Data Controller:</strong> WAVE DSP, Inc.<br />
        <strong>Email:</strong> <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a><br />
        <strong>Self-serve data deletion:</strong> <a href="/data-deletion">wavedsp.ai/data-deletion</a><br />
        <strong>Mailing address:</strong> available on request.
      </p>
      <p>
        EU and UK residents may also lodge a complaint with their local data-protection
        authority. California residents may contact the California Privacy Protection
        Agency. We&rsquo;d much rather solve it directly with you first &mdash; please
        write to us before escalating.
      </p>
    </LegalLayout>
  );
}
